import React, {useEffect} from 'react';

import {createGAEvent} from '@optimaxdev/analytics/desktop/lib/gaEvent/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {connect, useDispatch, useSelector} from 'react-redux';
import {Navigate} from 'react-router-dom';

import {SUCCESS_PAGE_POPUP_CLOSED} from 'constants/analytics/events/successPage';
import {useAdditionalPopups} from 'desktop/hooks/useAdditionalPopups/useAdditionalPopups';
import {useGetOrderLast} from 'desktop/hooks/useGetOrderLast/useGetOrderLast';
import {useGetTYPData} from 'desktop/hooks/useGetTYPData/useGetTYPData';
import {useSendDataLayer} from 'desktop/hooks/useSendDataLayer/useSendDataLayer';
import {useSendTYPAnalytics} from 'desktop/hooks/useSendTYPAnalytics/useSendTYPAnalytics';
import {useTrackingConversion} from 'desktop/hooks/useTrackingConversion/useTrackingConversion';
import {setCheckoutSideReview} from 'desktop/reducers/_checkoutSideReview';
import {ApplicationStoreType} from 'desktop/store';
import {useHostDispatch} from 'hooks/hostHooks/hostHooks';
import {useCheckLuxuryItem} from 'hooks/useCheckLuxuryItem/useCheckLuxuryItem';

import {flags, isUhcEnabled} from 'libs/rollout';

import {getEntireCheckout} from '../checkout/store/selectors/completeOrder/completeOrder';

import {SuccessRedesignConnected} from './containerV2/successRedesign/successRedesign';
import {setUpsellTimer} from './store/reducers/upsellTimer/upsellTimer';
import {isOrderContainsSomeContactLenses, getStatus} from './store/selectors/orderItems/orderItems';
import {SuccessPageConnected} from './success';

export type PropsType = {
    hasCartContactLenses: boolean;
    isOrderedItemsPending: boolean;
    skeleton?: React.ReactNode;
};

const useTopLevelHooks = () => {
    const dispatch = useDispatch();
    const checkoutSideReviewLocal = useSelector(getEntireCheckout);

    useAdditionalPopups();
    useSendDataLayer();
    useGetOrderLast();
    useSendTYPAnalytics();
    useTrackingConversion();
    useCheckLuxuryItem(flags.dLuxurySpecialExperience.isEnabled());

    useEffect(() => {
        dispatch(
            // @ts-expect-error problem with host and local type capability
            setCheckoutSideReview(checkoutSideReviewLocal),
        );
    }, [checkoutSideReviewLocal, dispatch]);

    useEffect(() => {
        const popupCloseListener = () => {
            dispatch(setUpsellTimer());
            pushDataLayer(createGAEvent(SUCCESS_PAGE_POPUP_CLOSED));
        };

        window.addEventListener('upsell:popupClose', popupCloseListener);

        return () => {
            window.removeEventListener('upsell:popupClose', popupCloseListener);
        };
    }, []);
};

/**
 * Success Page Wrapper
 * @param {PropsType} props - Component props
 * @return {React.ReactElement} component
 */
export const SuccessPageWrapper: React.FC<PropsType> = ({
    hasCartContactLenses,
    isOrderedItemsPending,
    skeleton = null,
}) => {
    const shouldRedirect = useGetTYPData();
    const dispatch = useHostDispatch();

    useTopLevelHooks();

    useEffect(() => {
        dispatch({
            payload: {
                view: isOrderedItemsPending || hasCartContactLenses ? 'cl' : 'default',
            },
            type: 'CHANGE_HEADER_VIEW',
        });
        return () => {
            dispatch({
                payload: {
                    view: 'default',
                },
                type: 'CHANGE_HEADER_VIEW',
            });
        };
    }, [isOrderedItemsPending, hasCartContactLenses, dispatch]);

    if (shouldRedirect) {
        return <Navigate to="/checkout/cart" />;
    }

    if (flags.dTYPContacts.isEnabled() && hasCartContactLenses && !isUhcEnabled())
        return <SuccessRedesignConnected skeleton={skeleton} />;

    return <SuccessPageConnected skeleton={skeleton} />;
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    hasCartContactLenses: isOrderContainsSomeContactLenses(store),
    isOrderedItemsPending: getStatus(store) === 'pending',
});

const SuccessPageWrapperConnected = connect(mapStateToProps)(SuccessPageWrapper);

export default SuccessPageWrapperConnected;
